import { Close, FormatAlignCenter, FormatAlignLeft, FormatAlignRight } from "@mui/icons-material";
import {
  AppBar,
  Box,
  Button,
  Divider,
  IconButton,
  TextField,
  Toolbar,
  Typography
} from "@mui/material";
import { useState } from "react";
import { ChartDataProps, ChartProps, GenericChart } from "../../graficos";
import { generateRandomColor, generateRandomHash, useWindowSize } from "../../../core";
import { TableData } from "./chartTable";

interface Props {
  startData?: ChartProps;
  onClose?: () => void;
  onInsert?: (chart: ChartProps) => void;
}

export const InsertChartPage: React.FC<Props> = ({ onClose, onInsert, startData }) => {
  const { height } = useWindowSize();

  const [graficData, setGraficData] = useState<ChartProps>(startData ?? {
    id: generateRandomHash(16),
    height: 300,
    width: 300,
    data: [],
    dataItems: [],
  });

  const handleFinish = () => {
    if (onInsert) onInsert(graficData);
  };
  const handleTitle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGraficData({ ...graficData, title: event.target.value });
  };
  const handleWidth = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === "") return;
    try {
      setGraficData({ ...graficData, width: parseFloat(event.target.value) });
    } catch (e) {
      //
    }
  };
  const handleHeight = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === "") return;
    try {
      setGraficData({ ...graficData, height: parseFloat(event.target.value) });
    } catch (e) {
      //
    }
  };

  const handleAlign = (position?: "center" | "start" | "end") => {
    setGraficData({ ...graficData, position: position });
  };

  const handleDataChange = (
    data: { [key: string]: number | string; }[],
    chartData: ChartDataProps[]
  ) => {
    const newGraficData = { ...graficData, data: data, dataItems: chartData } as ChartProps;
    setGraficData(newGraficData);
  };

  return <>
    {/* App Bar */}
    <AppBar sx={{ position: 'relative' }}>
      <Toolbar>
        <IconButton
          edge="start"
          color="inherit"
          onClick={onClose}
          aria-label="close"
        >
          <Close />
        </IconButton>
        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
          Gráfico
        </Typography>
        <Button autoFocus color="inherit" onClick={handleFinish}>
          {startData ? "Alterar" : "Inserir"}
        </Button>
      </Toolbar>
    </AppBar>

    {/* Conteúdo */}
    <Box sx={{
      display: "flex",
      flexDirection: "row",
      height: `${height - 76}px`,
    }}>

      {/* Opções do gráfico */}
      <Box sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        padding: "15px",
        width: "35vw"
      }}>
        {/* Título e posição do gráfico */}
        <div style={{
          height: "56px",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}>
          {/* Título */}
          <TextField
            fullWidth
            label="Título"
            variant="standard"
            defaultValue={graficData.title}
            onChange={handleTitle}
          />
          <div style={{ width: "7.5px" }} />
          <Divider orientation="vertical" />
          <div style={{ width: "7.5px" }} />

          {/* Tamanho */}
          <div style={{
            width: "350px",
            display: "flex",
            borderRadius: "5px",
            alignItems: "center",
          }}>
            <TextField
              label="width"
              value={graficData.width}
              onChange={handleWidth}
              variant="outlined"
              fullWidth
            />
            <div style={{ width: "15px" }} />
            <Typography>X</Typography>
            <div style={{ width: "15px" }} />
            <TextField
              label="height"
              value={graficData.height}
              onChange={handleHeight}
              variant="outlined"
              fullWidth
            />
          </div>
          <div style={{ width: "7.5px" }} />
          <Divider orientation="vertical" />
          <div style={{ width: "7.5px" }} />

          {/* Posição */}
          <div style={{
            height: "36px",
            display: "flex",
            borderRadius: "5px",
            border: "1px solid #EEE"
          }}>
            <IconButton
              onClick={() => handleAlign(graficData.position === "start" ? undefined : "start")}
              color={graficData.position === "start" ? "secondary" : undefined}
            ><FormatAlignLeft /></IconButton>
            <Divider orientation="vertical" />
            <IconButton
              onClick={() => handleAlign(graficData.position === "center" ? undefined : "center")}
              color={graficData.position === "center" ? "secondary" : undefined}
            ><FormatAlignCenter /></IconButton>
            <Divider orientation="vertical" />
            <IconButton
              onClick={() => handleAlign(graficData.position === "end" ? undefined : "end")}
              color={graficData.position === "end" ? "secondary" : undefined}
            ><FormatAlignRight /></IconButton>
          </div>
        </div>

        <div style={{ height: "15px" }} />

        {/* Tabela dos dados */}
        <Typography fontWeight={"bold"} fontSize={16}>Dados:</Typography>
        <Box sx={{
          height: "100%",
          display: 'flex',
          flexDirection: 'column',
          overflowY: "auto",
        }}>
          <TableData
            cellMinWidth={216}
            data={graficData.data}
            onDataChange={handleDataChange}
            dataItems={
              graficData.dataItems ?? Object.keys(graficData.data).map((key) => {
                return {
                  typeGraph: "bar",
                  fill: generateRandomColor(),
                  dataKey: key
                };
              })
            }
          />
        </Box>
      </Box>

      {/* Preview */}
      <Box sx={{
        width: "40vw",
        display: "flex",
        flexDirection: "column",
        padding: "15px",
        alignItems: "center",
      }}>
        <Typography fontWeight={"bold"} fontSize={18}>
          {graficData.title != "" ? (graficData.title ?? "Preview") : "Preview"}
        </Typography>

        <Box sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          padding: "15px",
          alignItems: graficData.position ?? "center",
        }}>
          <Box
            sx={{
              height: `${graficData.height}px`,
              width: `${graficData.width ?? 100}px`,
              border: "1px solid black"
            }}
          >
            <GenericChart {...graficData} />
          </Box>
        </Box>

      </Box>
    </Box >
  </>;
};